import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { SnapshotCompare } from "./SnapshotCompare";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Search,
  FileWarning,
  Image,
  FileText,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Globe,
  Filter,
  BarChart3,
  Folder
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quality score calculation
const getScoreColor = (score) => {
  if (score >= 80) return "text-green-400";
  if (score >= 60) return "text-yellow-400";
  if (score >= 40) return "text-orange-400";
  return "text-red-400";
};

const getScoreBg = (score) => {
  if (score >= 80) return "bg-green-500/10 border-green-500/30";
  if (score >= 60) return "bg-yellow-500/10 border-yellow-500/30";
  if (score >= 40) return "bg-orange-500/10 border-orange-500/30";
  return "bg-red-500/10 border-red-500/30";
};

const getStatusColor = (status) => {
  switch(status) {
    case 'COMPLETED': return 'text-green-400 bg-green-500/10';
    case 'SCANNING': return 'text-blue-400 bg-blue-500/10';
    case 'ANALYZING': return 'text-cyan-400 bg-cyan-500/10';
    case 'ERROR': return 'text-red-400 bg-red-500/10';
    default: return 'text-zinc-400 bg-zinc-500/10';
  }
};

// Grouped Issue Card Component - Groups similar issues together
const GroupedIssueCard = ({ issues, type, pattern, reason }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getIcon = () => {
    switch(type) {
      case 'alt': return <Image className="w-4 h-4 text-blue-400" />;
      case 'title': return <FileText className="w-4 h-4 text-purple-400" />;
      case 'desc': return <FileWarning className="w-4 h-4 text-orange-400" />;
      default: return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getTypeLabel = () => {
    switch(type) {
      case 'alt': return 'Alt Text';
      case 'title': return 'Meta Title';
      case 'desc': return 'Meta Description';
      default: return 'Issue';
    }
  };

  // Get unique sitemap types in this group
  const sitemapTypes = [...new Set(issues.map(i => i.sitemapType).filter(Boolean))];

  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-800/80 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {getIcon()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-zinc-500 font-mono">{getTypeLabel()}</span>
              <span className="text-xs px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">
                {reason}
              </span>
              {pattern && (
                <span className="text-xs px-1.5 py-0.5 bg-zinc-700 text-zinc-500 rounded font-mono">
                  "{pattern}"
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {sitemapTypes.length > 0 && (
            <div className="flex items-center gap-1">
              {sitemapTypes.slice(0, 3).map((st) => (
                <span key={st} className="text-xs px-1.5 py-0.5 bg-zinc-700/50 text-zinc-500 rounded font-mono">
                  {st}
                </span>
              ))}
              {sitemapTypes.length > 3 && (
                <span className="text-xs text-zinc-600">+{sitemapTypes.length - 3}</span>
              )}
            </div>
          )}
          <span className="text-sm font-mono text-yellow-500 bg-yellow-500/10 px-2 py-1 rounded">
            {issues.length}x
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </div>
      
      {expanded && (
        <div className="border-t border-zinc-700 max-h-64 overflow-y-auto">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/30 ${
              issue.issueType === 'oversized_image' ? 'bg-orange-900/10' : ''
            }`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-400 text-xs truncate font-mono" title={issue.url}>
                    {issue.url}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-zinc-500 text-xs truncate" title={issue.reason}>
                      <span className={issue.issueType === 'oversized_image' ? 'text-orange-400 font-medium' : ''}>
                        {issue.reason}
                      </span>
                    </p>
                  </div>
                  {issue.value && issue.issueType !== 'oversized_image' && (
                    <p className="text-zinc-600 text-xs truncate mt-0.5" title={issue.value}>
                      Value: {issue.value}
                    </p>
                  )}
                  {/* Show image details for alt text issues */}
                  {type === 'alt' && issue.imageSrc && (
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-zinc-600 text-xs truncate max-w-[200px]" title={issue.imageSrc}>
                        Image: {issue.imageSrc.split('/').pop()?.substring(0, 30) || 'Unknown'}
                      </span>
                      {issue.imageSizeFormatted && issue.imageSizeFormatted !== 'Unknown' && (
                        <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${
                          issue.imageSize > 500 * 1024 ? 'bg-red-500/20 text-red-400' :
                          issue.imageSize > 200 * 1024 ? 'bg-orange-500/20 text-orange-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {issue.imageSizeFormatted}
                        </span>
                      )}}
                      {issue.imageWidth && issue.imageHeight && (
                        <span className="text-xs px-1.5 py-0.5 bg-zinc-700/50 text-zinc-500 rounded font-mono">
                          {issue.imageWidth}×{issue.imageHeight}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {issue.sitemapType && (
                    <span className="text-xs px-1 py-0.5 bg-zinc-700/50 text-zinc-500 rounded font-mono">
                      {issue.sitemapType}
                    </span>
                  )}
                  {type === 'alt' && issue.imageSrc && (
                    <a 
                      href={issue.imageSrc.startsWith('http') ? issue.imageSrc : `${issue.url.split('/').slice(0, 3).join('/')}${issue.imageSrc}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-400"
                      onClick={(e) => e.stopPropagation()}
                      title="View image"
                    >
                      <Image className="w-3 h-3" />
                    </a>
                  )}
                  <a 
                    href={issue.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-zinc-500 hover:text-zinc-300"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to group issues by pattern/reason
const groupIssues = (issues) => {
  const groups = {};
  issues.forEach(issue => {
    const key = `${issue.reason}|${issue.matchedPattern || ''}`;
    if (!groups[key]) {
      groups[key] = {
        reason: issue.reason,
        pattern: issue.matchedPattern,
        issues: []
      };
    }
    groups[key].issues.push(issue);
  });
  // Sort by count descending
  return Object.values(groups).sort((a, b) => b.issues.length - a.issues.length);
};

// Site Card Component
const SiteCard = ({ site, onAnalyze, onDelete, isAnalyzing }) => {
  const [showIssues, setShowIssues] = useState(false);
  const [issueFilter, setIssueFilter] = useState('all');
  const [sitemapTypeFilter, setSitemapTypeFilter] = useState('all');
  
  const qualityData = site.quality_data;
  const score = site.quality_score;
  const sitemapTypes = site.sitemap_types || [];
  
  // Filter by sitemap type first, then by issue type
  const filterBySitemapType = (issues) => {
    if (!issues) return [];
    if (sitemapTypeFilter === 'all') return issues;
    return issues.filter(issue => issue.sitemapType === sitemapTypeFilter);
  };
  
  const altIssuesFiltered = filterBySitemapType(qualityData?.altIssues);
  const titleIssuesFiltered = filterBySitemapType(qualityData?.titleIssues);
  const descIssuesFiltered = filterBySitemapType(qualityData?.descIssues);
  
  // Count oversized images (>200KB)
  const oversizedImagesCount = altIssuesFiltered.filter(i => i.issueType === 'oversized_image').length;
  const altTextIssuesCount = altIssuesFiltered.filter(i => i.issueType !== 'oversized_image').length;
  
  const filteredIssues = {
    alt: issueFilter === 'all' || issueFilter === 'alt' ? altIssuesFiltered : 
         issueFilter === 'oversized' ? altIssuesFiltered.filter(i => i.issueType === 'oversized_image') : [],
    title: issueFilter === 'all' || issueFilter === 'title' ? titleIssuesFiltered : [],
    desc: issueFilter === 'all' || issueFilter === 'desc' ? descIssuesFiltered : [],
  };

  const totalIssues = qualityData ? 
    (qualityData.altIssues?.length || 0) + (qualityData.titleIssues?.length || 0) + (qualityData.descIssues?.length || 0) : 0;
  const totalFilteredBySitemapType = altIssuesFiltered.length + titleIssuesFiltered.length + descIssuesFiltered.length;
  const totalFiltered = filteredIssues.alt.length + filteredIssues.title.length + filteredIssues.desc.length;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`quality-site-${site.id}`}>
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-yellow-400" />
            <div>
              <h3 className="text-white font-semibold">{site.name}</h3>
              <p className="text-zinc-500 text-xs truncate max-w-xs">{site.sitemap_url}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(site.status)}`}>
              {site.status}
            </span>
            {score !== null && score !== undefined && (
              <div className={`px-3 py-1.5 rounded-lg border ${getScoreBg(score)}`}>
                <span className={`text-lg font-bold ${getScoreColor(score)}`}>{score}</span>
                <span className="text-zinc-500 text-xs ml-1">/ 100</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Row */}
      {qualityData && (
        <div className="px-4 py-3 bg-zinc-800/30 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm flex-wrap">
              <div className="flex items-center gap-2">
                <Image className="w-4 h-4 text-blue-400" />
                <span className="text-zinc-400">{altTextIssuesCount} alt issues</span>
              </div>
              {oversizedImagesCount > 0 && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  <span className="text-orange-400 font-medium">{oversizedImagesCount} oversized images (&gt;200KB)</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-400" />
                <span className="text-zinc-400">{qualityData.titleIssues?.length || 0} title issues</span>
              </div>
              <div className="flex items-center gap-2">
                <FileWarning className="w-4 h-4 text-orange-400" />
                <span className="text-zinc-400">{qualityData.descIssues?.length || 0} desc issues</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Sitemap Type Filter */}
              {sitemapTypes.length > 1 && (
                <div className="flex items-center gap-2">
                  <Filter className="w-3 h-3 text-zinc-500" />
                  <select
                    value={sitemapTypeFilter}
                    onChange={(e) => setSitemapTypeFilter(e.target.value)}
                    className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 font-mono"
                    data-testid="sitemap-type-filter"
                  >
                    <option value="all">All Types</option>
                    {sitemapTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <span className="text-zinc-600 text-xs">
                {site.total_pages || 0} pages scanned
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-3 bg-zinc-800/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SnapshotCompare 
            siteId={site.id} 
            siteName={site.name} 
            monitorType="metaalt_quality"
            buttonClassName="text-zinc-400 hover:text-white border-zinc-700 rounded-lg text-sm font-mono"
          />
          <button
            onClick={() => onAnalyze(site.id)}
            disabled={isAnalyzing || site.status === 'SCANNING'}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-mono transition-colors disabled:opacity-50"
          >
            {isAnalyzing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            {qualityData ? 'Re-analyze' : 'Analyze Quality'}
          </button>
          
          {qualityData && totalIssues > 0 && (
            <button
              onClick={() => setShowIssues(!showIssues)}
              className="flex items-center gap-2 px-3 py-2 text-zinc-400 hover:text-white border border-zinc-700 rounded-lg text-sm font-mono transition-colors"
            >
              {showIssues ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {totalIssues} Issues
            </button>
          )}
        </div>

        <button
          onClick={() => onDelete(site.id)}
          className="flex items-center gap-2 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-950/30 rounded-lg text-sm font-mono transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Issues List */}
      {showIssues && qualityData && (
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <span className="text-zinc-400 text-sm">
              Quality Issues
              {sitemapTypeFilter !== 'all' && (
                <span className="text-yellow-500 ml-2">
                  ({sitemapTypeFilter}: {totalFilteredBySitemapType} of {totalIssues})
                </span>
              )}
            </span>
            <select
              value={issueFilter}
              onChange={(e) => setIssueFilter(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-300"
            >
              <option value="all">All Issues ({totalFilteredBySitemapType})</option>
              <option value="alt">Alt Text ({altTextIssuesCount})</option>
              {oversizedImagesCount > 0 && (
                <option value="oversized">Oversized Images ({oversizedImagesCount})</option>
              )}
              <option value="title">Titles ({titleIssuesFiltered.length})</option>
              <option value="desc">Descriptions ({descIssuesFiltered.length})</option>
            </select>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {totalFiltered === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-zinc-400">No issues in this category!</p>
              </div>
            ) : (
              <>
                {groupIssues(filteredIssues.alt).map((group, idx) => (
                  <GroupedIssueCard 
                    key={`alt-${idx}`} 
                    issues={group.issues} 
                    type="alt" 
                    pattern={group.pattern}
                    reason={group.reason}
                  />
                ))}
                {groupIssues(filteredIssues.title).map((group, idx) => (
                  <GroupedIssueCard 
                    key={`title-${idx}`} 
                    issues={group.issues} 
                    type="title" 
                    pattern={group.pattern}
                    reason={group.reason}
                  />
                ))}
                {groupIssues(filteredIssues.desc).map((group, idx) => (
                  <GroupedIssueCard 
                    key={`desc-${idx}`} 
                    issues={group.issues} 
                    type="desc" 
                    pattern={group.pattern}
                    reason={group.reason}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Last analyzed */}
      {site.last_analyzed && (
        <div className="px-4 py-2 border-t border-zinc-800">
          <p className="text-zinc-600 text-xs">
            Last analyzed: {new Date(site.last_analyzed).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

// Main Component
export function MetaAltQuality() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSitemapUrl, setNewSitemapUrl] = useState('');
  const [newSiteName, setNewSiteName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("all");

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/groups`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error("Failed to fetch groups:", error);
    }
  }, []);

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const fetchSites = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/metaalt-quality`);
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error("Failed to fetch sites:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
    fetchGroups();
  }, [fetchSites, fetchGroups]);

  const addSite = async (e) => {
    e.preventDefault();
    if (!newSitemapUrl.trim()) {
      toast.error("Please enter a sitemap URL");
      return;
    }

    setIsAdding(true);
    try {
      const response = await fetch(`${API_URL}/api/metaalt-quality`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sitemap_url: newSitemapUrl,
          name: newSiteName || undefined,
          groups: selectedGroups
        })
      });

      if (response.ok) {
        toast.success("Site added successfully");
        setNewSitemapUrl('');
        setNewSiteName('');
        setSelectedGroups([]);
        setShowAddForm(false);
        fetchSites();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add site");
      }
    } catch (error) {
      toast.error("Failed to add site");
    } finally {
      setIsAdding(false);
    }
  };

  const analyzeQuality = async (siteId) => {
    setAnalyzingId(siteId);
    try {
      const response = await fetch(`${API_URL}/api/metaalt-quality/${siteId}/analyze`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        const totalIssues = (data.altIssues?.length || 0) + (data.titleIssues?.length || 0) + (data.descIssues?.length || 0);
        toast.success(`Analysis complete: Found ${totalIssues} quality issues`);
        fetchSites();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to analyze quality");
      }
    } catch (error) {
      toast.error("Failed to analyze quality");
    } finally {
      setAnalyzingId(null);
    }
  };

  const deleteSite = async (siteId) => {
    if (!window.confirm("Delete this site and all analysis data?")) return;

    try {
      const response = await fetch(`${API_URL}/api/metaalt-quality/${siteId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success("Site deleted");
        fetchSites();
      } else {
        toast.error("Failed to delete site");
      }
    } catch (error) {
      toast.error("Failed to delete site");
    }
  };

  const filteredSites = sites.filter(site => {
    // Group filter
    if (filterGroup !== "all") {
      if (!site.groups || !site.groups.includes(filterGroup)) {
        return false;
      }
    }
    // Search filter
    return (
      site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.sitemap_url.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="metaalt-quality-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-yellow-400" />
            MetaAlt Quality Monitor
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Detect low-quality meta titles, descriptions, and alt text
          </p>
        </div>

        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-mono text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Site
        </button>
      </div>

      {/* Add Site Form */}
      {showAddForm && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <h3 className="text-white font-medium mb-3">Add Site for Quality Analysis</h3>
          <form onSubmit={addSite} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm block mb-2">Sitemap URL *</label>
                <input
                  type="text"
                  placeholder="https://example.com/sitemap.xml"
                  value={newSitemapUrl}
                  onChange={(e) => setNewSitemapUrl(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-yellow-500"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm block mb-2">Site Name (optional)</label>
                <input
                  type="text"
                  placeholder="My Website"
                  value={newSiteName}
                  onChange={(e) => setNewSiteName(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-yellow-500"
                />
              </div>
            </div>
            {/* Group Selection */}
            {groups.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-zinc-500 font-mono">Assign to groups:</span>
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => toggleGroupSelection(group.id)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono transition-colors ${
                      selectedGroups.includes(group.id)
                        ? "bg-zinc-700 text-white"
                        : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                    }`}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} />
                    {group.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isAdding}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-mono transition-colors disabled:opacity-50"
              >
                {isAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add & Scan
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewSitemapUrl('');
                  setNewSiteName('');
                  setSelectedGroups([]);
                }}
                className="px-4 py-2 text-zinc-400 hover:text-white rounded-lg text-sm font-mono transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-4">
        <p className="text-zinc-400 text-sm">
          <strong className="text-zinc-300">Quality Checks:</strong>
        </p>
        <ul className="text-zinc-500 text-sm mt-2 space-y-1">
          <li className="flex items-center gap-2">
            <Image className="w-4 h-4 text-blue-400" />
            <span><strong>Alt Text:</strong> Detects generic text like "untitled", "image", "screenshot", file extensions</span>
          </li>
          <li className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-purple-400" />
            <span><strong>Meta Titles:</strong> Flags titles with only "Home", "About", "Lorem", or too short</span>
          </li>
          <li className="flex items-center gap-2">
            <FileWarning className="w-4 h-4 text-orange-400" />
            <span><strong>Meta Descriptions:</strong> Finds placeholder text, lorem ipsum, or very short descriptions</span>
          </li>
        </ul>
      </div>

      {/* Search and Group Filter */}
      {sites.length > 0 && (
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              placeholder="Search sites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-3 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-yellow-500"
            />
          </div>
          {groups.length > 0 && (
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-sm text-zinc-300 font-mono focus:outline-none focus:border-yellow-500"
              data-testid="metaalt-group-filter"
            >
              <option value="all">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Sites List */}
      {filteredSites.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900 rounded-lg border border-zinc-800">
          <AlertTriangle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg text-zinc-300 font-medium">No Sites Yet</h3>
          <p className="text-zinc-500 text-sm mt-1 mb-4">
            Add a site using its sitemap URL to start analyzing meta quality
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-mono text-sm"
          >
            <Plus className="w-4 h-4 inline mr-2" />
            Add Your First Site
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSites.map(site => (
            <SiteCard
              key={site.id}
              site={site}
              onAnalyze={analyzeQuality}
              onDelete={deleteSite}
              isAnalyzing={analyzingId === site.id}
            />
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {sites.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-yellow-400" />
                <span className="text-zinc-300 font-medium">{sites.length} Sites</span>
              </div>
              <div className="text-zinc-500 text-sm">
                {sites.filter(s => s.quality_score !== null && s.quality_score !== undefined).length} analyzed
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-400">{sites.filter(s => s.quality_score >= 80).length} healthy</span>
              <span className="text-yellow-400">{sites.filter(s => s.quality_score >= 40 && s.quality_score < 80).length} warning</span>
              <span className="text-red-400">{sites.filter(s => s.quality_score !== null && s.quality_score < 40).length} critical</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MetaAltQuality;
